import { DataTable, TableMenu } from '@/components'
import { cn } from '@/lib/utils'
import type { ResultNode } from '@/types/util'
import {
  createCustomSorter,
  PERMISSIONS,
  truncate,
  usePermissions,
} from '@/utils'
import { createColumnHelper } from '@tanstack/react-table'
import type { SortingState } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { subSeconds } from 'date-fns'
import { useCallback, useMemo, useRef } from 'react'
import { Link } from 'react-router'
import type { USER_ABSENCES_QUERY } from '../queries'
import type { AbsenceTypeNode } from '../types.graphql'
import type { AbsenceDialogRef } from './absence-dialog'
import { AbsenceDialog } from './absence-dialog'
import { useOldAbsenceHandler } from './handler.hooks'
import { AbsenceTypeHeader, DateCell, StatusCell } from './table-cells'
import {
  IconContainer,
  StyledIcon,
} from './TimesheetTableRow/TimesheetTableRow'

export type AbsenceNode = ResultNode<typeof USER_ABSENCES_QUERY, 'allAbsences'>

interface Props {
  absences: AbsenceNode[]

  createLockedBefore: Date | null
  selectedAbsenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  initialOrderBy?: string
  onOrderByChange?(orderBy: string): void

  onSelectAbsenceType: (
    absenceType: Pick<AbsenceTypeNode, 'id' | 'name'> | null
  ) => void
}

export function AbsencesTable({
  absences,

  createLockedBefore,
  selectedAbsenceType,
  initialOrderBy,
  onOrderByChange,

  onSelectAbsenceType,
}: Props) {
  const translations = useTranslate({
    link: 'common.link',
    employee: 'common.employee',
    dayStart: 'common.day-start',
    dayEnd: 'common.day-end',
    absenceType: 'timesheets.absence-type',
    reason: 'common.reason',
    status: 'common.status',

    open: 'common.open',
    items: ['absences.table.num-items', { n: 0 }],
  })

  const absenceDialogRef = useRef<AbsenceDialogRef>(null)
  const absenceHandler = useOldAbsenceHandler(absenceDialogRef)

  const { hasPermissions } = usePermissions()
  const canAddOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.othersabsence,
    false
  )

  const statusSorter = createCustomSorter([
    'PENDING',
    'REJECTED',
    'APPROVED',
  ] as const)

  const column = createColumnHelper<AbsenceNode>()

  const tableColumns = useMemo(
    () => [
      column.display({
        id: 'link',
        header: translations.link,
        meta: {
          narrow: true,
        },
        cell(info) {
          const { id } = info.row.original

          return (
            <Link className="hover:text-file-onedrive" to={`/absences/${id}`}>
              {translations.open}
            </Link>
          )
        },
      }),
      column.accessor('user', {
        header: translations.employee,
        meta: {
          narrow: true,
        },
        enableSorting: true,
        cell(info) {
          const user = info.getValue()

          return (
            <Link
              className="text-inherit"
              to={`/users/${user.id}`}
              target="_blank"
            >
              {user.fullName}
            </Link>
          )
        },
      }),
      column.accessor('datetimeStart', {
        header: translations.dayStart,
        meta: {
          narrow: true,
        },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.datetimeStart).getTime() -
          new Date(b.original.datetimeStart).getTime(),
        cell(info) {
          const startDate = new Date(info.getValue())
          return <DateCell date={startDate} side="start" type="absence" />
        },
      }),
      column.accessor('datetimeEnd', {
        header: translations.dayEnd,
        meta: {
          narrow: true,
          end: true,
        },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.datetimeEnd).getTime() -
          new Date(b.original.datetimeEnd).getTime(),
        cell(info) {
          const endDate = subSeconds(new Date(info.getValue()), 1)
          return <DateCell date={endDate} side="end" type="absence" />
        },
      }),
      column.accessor('absenceType', {
        enableSorting: true,
        sortingFn: (a, b) => {
          const aName = a.original.absenceType?.name || ''
          const bName = b.original.absenceType?.name || ''

          return aName.localeCompare(bName)
        },
        header: () => {
          return (
            <AbsenceTypeHeader
              absenceType={selectedAbsenceType}
              onRemove={() => onSelectAbsenceType(null)}
            >
              {translations.absenceType}
            </AbsenceTypeHeader>
          )
        },
        meta: { narrow: true },
        cell(info) {
          const absenceType = info.getValue()
          if (!absenceType) return '-'

          return (
            <div
              className={cn(
                'cursor-pointer',
                !!selectedAbsenceType
                  ? 'hover:text-red-500'
                  : 'hover:text-secondary'
              )}
              role="button"
              onClick={() =>
                onSelectAbsenceType(!!selectedAbsenceType ? null : absenceType)
              }
            >
              {absenceType.name}
            </div>
          )
        },
      }),
      column.accessor('reason', {
        header: translations.reason,
        cell(info) {
          const reason = info.getValue()
          return truncate(reason, 50)
        },
      }),
      column.accessor('commentCount', {
        header: 'Comments',
        meta: { narrow: true, center: true },
        cell: info => {
          const commentCount = info.getValue()

          const id = info.row.id

          return (
            <Link
              className="flex justify-center no-underline hover:no-underline text-current hover:text-secondary"
              to={`/absences/${id}`}
            >
              <span className="flex gap-2 [&>*:hover]:text-secondary">
                {commentCount}

                <IconContainer>
                  <StyledIcon icon="comment-alt-lines" size="16px" />
                </IconContainer>
              </span>
            </Link>
          )
        },
      }),
      column.accessor('requestStatus', {
        header: translations.status,
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          statusSorter(a.original.requestStatus, b.original.requestStatus),
        cell(info) {
          const requestStatus = info.getValue()
          return <StatusCell status={requestStatus} />
        },
      }),
      column.display({
        id: 'menu',
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const absence = info.row.original
          const items = absenceHandler.createAbsenceMenuItems(absence, true)

          return <TableMenu items={items} />
        },
      }),
    ],
    [
      absenceHandler,
      column,
      onSelectAbsenceType,
      selectedAbsenceType,
      statusSorter,
      translations.absenceType,
      translations.dayEnd,
      translations.dayStart,
      translations.employee,
      translations.link,
      translations.open,
      translations.reason,
      translations.status,
    ]
  )

  const initialSort = useMemo<SortingState>(() => {
    if (!initialOrderBy) return [{ id: 'requestStatus', desc: false }]

    const desc = initialOrderBy.startsWith('-')
    const key = desc ? initialOrderBy.slice(1) : initialOrderBy

    const mappedId = (() => {
      if (key === 'status' || key === 'requestStatus') return 'requestStatus'
      if (key === 'start' || key === 'datetimeStart') return 'datetimeStart'
      if (key === 'end' || key === 'datetimeEnd') return 'datetimeEnd'
      if (key === 'user') return 'user'
      if (key === 'absenceType') return 'absenceType'
      if (key === 'reason') return 'reason'
      if (key === 'comments' || key === 'commentCount') return 'commentCount'
      return null
    })()

    if (!mappedId) return [{ id: 'requestStatus', desc: false }]
    return [{ id: mappedId, desc }]
  }, [initialOrderBy])

  const mapColumnIdToOrderBy = (id: string) => {
    if (id === 'requestStatus') return 'status'
    if (id === 'datetimeStart') return 'start'
    if (id === 'datetimeEnd') return 'end'
    if (id === 'user') return 'user'
    if (id === 'absenceType') return 'absenceType'
    if (id === 'reason') return 'reason'
    if (id === 'commentCount') return 'comments'
    return null
  }

  const handleSort = useCallback(
    (sorting: SortingState) => {
      const next = sorting[0]
      if (!next) {
        onOrderByChange?.('start')
        return
      }

      const key = mapColumnIdToOrderBy(next.id)
      if (!key) return

      onOrderByChange?.(next.desc ? `-${key}` : key)
    },
    [onOrderByChange]
  )

  return (
    <>
      <AbsenceDialog
        ref={absenceDialogRef}
        hideTrigger
        createLockedBefore={createLockedBefore}
        canAddForOthers={canAddOthersAbsence}
      />

      <DataTable
        columns={tableColumns}
        data={absences}
        pagination={{
          itemsText: n => translations.items({ n }),
        }}
        initialSort={initialSort}
        onSortingChange={handleSort}
        inCard
      />
    </>
  )
}
